import api from '@/api';
import { mapFromApi } from '@/mappers';
import type { AppUser, LoanApplication } from '@/types';
import { computed, ref } from 'vue';
import { FeatureFlag, useFeatureFlags } from '@/helpers/useFeatureFlags';
import { applyScenarioBorrowerFinancials } from '@/mappers/mapFromApi/scenario-borrower-financials';
import { scenarioBorrowerPrefill } from '@/mappers/mapFromApi/borrower';
import type { ApplicationDto } from '@/api/backend/users/applications';
import type { BrokerRetrieveScenarioResponse } from '@/api/backend/brokers/scenarios/broker-retrieve-scenario';

class ConversionException extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ConversionException';
  }
}

export function useScenarioApi() {
  const isProcessing = ref(0);
  const { isEnabled } = useFeatureFlags();
  // Borrower groups are only submitted with the contact page redesign, so pre-filling without it would drop them.
  const isBorrowerPrefillEnabled = () =>
    isEnabled(FeatureFlag.COMPANY_TRUST_SCENARIO) && isEnabled(FeatureFlag.CONTACT_PAGE_REDESIGN);
  const retrieveBrokerScenario = async (
    appId: string,
  ): Promise<{
    success: boolean;
    error?: string;
    value?: LoanApplication;
  }> => {
    return await retrieveScenario(appId, 'broker');
  };

  const retrieveCustomerScenario = async (
    appId: string,
  ): Promise<{
    success: boolean;
    error?: string;
    value?: LoanApplication;
  }> => {
    return await retrieveScenario(appId, 'customer');
  };

  const retrieveScenario = async (
    appId: string,
    type: 'broker' | 'customer',
  ): Promise<{
    success: boolean;
    error?: string;
    value?: LoanApplication;
  }> => {
    isProcessing.value++;
    try {
      const scenarioApi = api.backend.brokers.scenario;
      const response =
        type === 'broker'
          ? await scenarioApi.retrieveBrokerScenario({ id: appId })
          : await scenarioApi.retrieveCustomerScenario({ id: appId });
      const application = response.data.application as ApplicationDto;
      let appUser: AppUser = 'Broker';
      if (type === 'customer') {
        appUser = 'Primary'; // primary always represents customer as user
      }
      try {
        const expenseTypeResponse = await api.backend.public.listExpenseTypes(application.structuralVersionNumber);
        const expenseTypes = expenseTypeResponse.expenseTypes ?? [];
        const applicationProgress = mapFromApi.applicationProgress(application, expenseTypes, appUser);
        if (type === 'broker' && isBorrowerPrefillEnabled()) {
          const { borrowers } = (response.data as BrokerRetrieveScenarioResponse).application;
          if (borrowers.length) {
            const prefill = scenarioBorrowerPrefill(borrowers, application.applicants, application.propertiesOwnership);
            applicationProgress.borrowerGroups = prefill.groups;
            applyScenarioBorrowerFinancials(applicationProgress, application, prefill);
          }
        }
        return { success: true, value: applicationProgress };
      } catch (e) {
        throw new ConversionException('Cannot convert payload into application object');
      }
    } catch (error) {
      if (error instanceof ConversionException) {
        return { success: false, error: 'conversion-fail' };
      } else {
        return { success: false, error: 'network-error' };
      }
    } finally {
      isProcessing.value--;
    }
  };

  return {
    isLoading: computed(() => isProcessing.value > 0),
    retrieveBrokerScenario,
    retrieveCustomerScenario,
  };
}
